import { ReactNode, useEffect, useRef, useState, useMemo, useContext, useId } from "react";
import NoAccommodationsImage from "../images/no-students-accommodated.svg?react";
import NoStudentsImage from "../images/no-students-available.svg?react";
import NoResults from "../../core/no-results/images/no-results.svg?react";
import ThreeDots from "../images/three-dots-vertical.svg?react";
import CaretUp from "../images/caretUp.svg?react";
import CaretDown from "../images/caretDown.svg?react";
import { Box, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import {
	CoreCheckbox,
	CoreModal,
	CorePagination,
	CorePaper,
	CoreSpinner,
	coreToast,
	CoreMenuItem,
	CoreButton,
	CoreSearchField,
	CoreMenuButton,
	CoreChip,
	CoreTitleCard,
	CoreSearchFieldPaper,
} from "../../core";
import { getScrollbarThemeOverrides } from "../../core/scrollbar";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import styles from "./exemptionModal.module.css";
import { ExemptedStudent, VerityInstituteSettings } from "../../types/common";
import SelectAccommodationModal from "./selectAccommodationModal";
import {
	ProctoringOption,
	ProctoringOptionProfileV2,
	Application,
	OptionRelatedInstituteSettings,
	Extension,
} from "@verity/proctoring-option/types";
import {
	categorizeProctoringOptions,
	ConfigurationSummaryList,
	NestedOption,
} from "../proctoring-options/configurationSummaryList";
import { VerityApiContext } from "../../api/fetch";
import i18nPackage from "../../i18n/i18n";
import { getPlatformTextFromProctoringOptions } from "../proctoring-options/option-text";
import { DEFAULT_PROCTORING_CONFIGURATION_OPTIONS, enforceConfiguration } from "@verity/proctoring-option";

const PAGE_SIZE = 18;

export type ExemptionModalProps = {
	courseId?: string;
	loading?: boolean;
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	hasInstituteProfilePermissions: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings: VerityInstituteSettings;
	enforcedProfilesOnly: boolean;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	exemptionInfo: {
		nonExemptRoster: Map<number, ExemptedStudent>;
		exemptRoster: Map<number, ExemptedStudent>;
	};
	setExemptionInfo: React.Dispatch<
		React.SetStateAction<{
			nonExemptRoster: Map<number, ExemptedStudent>;
			exemptRoster: Map<number, ExemptedStudent>;
		}>
	>;
	setStudentsLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
	editInstitutionExemptions?: boolean;
};

function StudentRow({
	student,
	selected,
	onCheckboxChange,
	action,
	isExempted,
	removeAction,
	profilesMap,
	hideBottomBorder,
	editInstitutionExemptions,
}: {
	student: ExemptedStudent;
	selected: boolean;
	onCheckboxChange: (studentId: number, checked: boolean) => void;
	action: ReactNode;
	isExempted: boolean;
	removeAction?: ReactNode;
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	hideBottomBorder: boolean;
	editInstitutionExemptions?: boolean;
}) {
	const textRef = useRef<HTMLDivElement>(null);
	const [overflow, setOverflow] = useState(false);
	const studentLabelId = useId();

	useEffect(() => {
		if (textRef.current) {
			setOverflow(textRef.current.offsetWidth < textRef.current.scrollWidth);
		}
	}, [student.name]);

	let studentName = (
		<ListItemText
			className={styles.studentName}
			style={isExempted ? { maxWidth: "135px" } : {}}
			primaryTypographyProps={{ ref: textRef }}
			primary={student.name}
			id={studentLabelId}
		/>
	);
	if (overflow) {
		studentName = (
			<Tooltip title={student.name} arrow placement="top">
				{studentName}
			</Tooltip>
		);
	}

	const getProfileColor = (profileName: string): string => {
		const colors = ["#D3DDF2", "#F9E4F5", "#E4E3F6", "#F8E4DD", "#FAEFDE", "#DFF2F2", "#F8DCDB", "#DBE7E1", "#E6E6E6"];
		const index = profileName.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
		return colors[index];
	};

	const profileName =
		isExempted && student.proctoringOptionProfileId
			? profilesMap.get(student.proctoringOptionProfileId)?.name ||
				i18nPackage.t("editQuizPage.accommodationsStep.unknownProfile")
			: i18nPackage.t("editQuizPage.accommodationsStep.unknownProfile");

	const profileColor = getProfileColor(profileName);
	const body = (
		<ListItem className={styles.studentRow} style={hideBottomBorder ? { borderBottom: "none" } : {}}>
			<div className={styles.checkboxContainer}>
				<CoreCheckbox
					className={styles.checkbox}
					checked={selected}
					disabled={student.type === "institution" && !editInstitutionExemptions}
					onChange={(e) => {
						onCheckboxChange(student.userId, e.target.checked);
					}}
					inputProps={{ "aria-labelledby": studentLabelId }}
				/>
				{studentName}
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
				{isExempted ? (
					student.proctoringOptionProfileId ? (
						<div style={{ background: "none", padding: 0, margin: 0, border: 0, height: "28px" }}>
							<CoreChip
								variant="rectangular"
								tooltip={profileName}
								chipColor={profileColor}
								className={styles.profileBadgeTag}
								tooltipProps={{
									placement: "top",
								}}
							>
								{profileName}
							</CoreChip>
						</div>
					) : (
						<div style={{ background: "none", padding: 0, margin: 0, border: 0, height: "28px" }}>
							<CoreChip
								chipColor="#F9E4F5"
								variant="rectangular"
								className={styles.notProctoredBadge}
								tooltip={i18nPackage.t("editQuizPage.accommodationsStep.notProctored")}
								tooltipProps={{
									placement: "top",
								}}
							>
								{i18nPackage.t("editQuizPage.accommodationsStep.notProctored")}
							</CoreChip>
						</div>
					)
				) : undefined}

				{action}
				{isExempted && removeAction}
			</div>
		</ListItem>
	);
	return (
		<>
			{student.type === "institution" && !editInstitutionExemptions ? (
				<Tooltip title={i18nPackage.t("editQuizPage.accommodationsStep.institutionToolTip")}>{body}</Tooltip>
			) : (
				body
			)}
		</>
	);
}

function StudentList({
	id,
	students,
	action,
	actions,
	buttonIcon,
	currPage,
	setPage,
	isExempted,
	removeAction,
	buttonTitle,
	title,
	noResultsHeader,
	noResultsBody,
	noResultsImage,
	profilesMap,
	setSelectedStudents,
	setAccommodateModalShowing,
	paginationAriaLabel,
	editInstitutionExemptions,
}: {
	id?: string;
	students: Array<ExemptedStudent>;
	buttonIcon?: ReactNode;
	currPage: number;
	setPage: (page: number) => void;
	isExempted: boolean;
	removeAction?: (student: ExemptedStudent) => void;
	buttonTitle: string;
	title: string;
	noResultsHeader: string;
	noResultsBody: string;
	noResultsImage: ReactNode;
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	setSelectedStudents: (students: Array<ExemptedStudent>) => void;
	setAccommodateModalShowing?: (bool: boolean) => void;
	paginationAriaLabel: string;
	editInstitutionExemptions?: boolean;
} & (
	| {
			action: (students: ExemptedStudent[]) => void;
			actions?: undefined;
	  }
	| {
			action?: undefined;
			actions: Array<{
				name: string;
				hide?: boolean | ((student: ExemptedStudent) => boolean);
				action: (student: ExemptedStudent) => void;
			}>;
	  }
)) {
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
	const isMobileLayout = useMobileLayout();

	const sortedStudents = useMemo(() => {
		if (sortDirection === "asc") {
			return [...students].sort((a, b) => a.name.localeCompare(b.name));
		} else if (sortDirection === "desc") {
			return [...students].sort((a, b) => b.name.localeCompare(a.name));
		}
		return students;
	}, [students, sortDirection]);

	const handleSelect = (studentId: number, checked: boolean) => {
		setSelected((prev) => {
			const newSet = new Set(prev);
			if (checked) {
				newSet.add(studentId);
			} else {
				newSet.delete(studentId);
			}
			return newSet;
		});
	};

	const handleSelectAll = (checked: boolean) => {
		if (isIndeterminate) {
			setSelected(new Set());
		} else {
			setSelected(
				() =>
					new Set(
						checked
							? students.filter((student) => student.type !== "institution").map((student) => student.userId)
							: [],
					),
			);
		}
	};

	const allSelected = selected.size === students.length && students.length > 0;
	const isIndeterminate = selected.size > 0 && selected.size < students.length;

	const totalPages = Math.ceil(students.length / PAGE_SIZE);

	useEffect(() => {
		setSelected((prevSelected) => {
			const updatedSelected = new Set(prevSelected);
			prevSelected.forEach((studentId) => {
				const studentExists = students.some((student) => student.userId === studentId);
				if (!studentExists) {
					updatedSelected.delete(studentId);
				}
			});
			return updatedSelected;
		});

		if (students.length <= currPage * PAGE_SIZE) {
			setPage(Math.max(totalPages - 1, 0));
		}
	}, [students]);

	const startingIndex = currPage * PAGE_SIZE;

	const visibleStudents = sortedStudents.slice(startingIndex, startingIndex + PAGE_SIZE);
	const fullPage = visibleStudents.length >= 6;

	return (
		<>
			<div className={styles.headerRow}>
				{selected.size !== 0 ? (
					<div className={styles.headerSelected}>
						<span>
							{i18nPackage.t("editQuizPage.accommodationsStep.studentList.selectedTestTakers", {
								count: selected.size,
							})}
						</span>
					</div>
				) : (
					""
				)}
				<div className={styles.headerButton}>
					<CoreButton
						color="primary"
						disabled={selected.size === 0}
						onClick={() => {
							if (action && setAccommodateModalShowing) {
								const selectedStudentsArray = Array.from(selected)
									.map((studentId) => students.find((s) => s.userId === studentId))
									.filter((student): student is ExemptedStudent => student !== undefined);
								setSelectedStudents(selectedStudentsArray);
								setAccommodateModalShowing(true);
							} else if (removeAction) {
								selected.forEach((studentId) => {
									const student = students.find((s) => s.userId === studentId);
									if (student) {
										removeAction(student);
									}
								});
							}
						}}
					>
						<div className={styles.header}>{buttonTitle}</div>
					</CoreButton>
				</div>
			</div>
			<div className={styles.listPanel}>
				<div id={id} className={styles.listWrapper}>
					<Box
						className={styles.scrollContainer}
						style={
							visibleStudents.length > 6
								? { height: "320.6px" }
								: {
										height: "320.6px",
										overflowY: "hidden",
									}
						}
						sx={{ ...getScrollbarThemeOverrides(10, 3) }}
					>
						<div className={styles.titleRow}>
							<div className={styles.titleContainer}>
								<CoreCheckbox
									className={styles.checkbox}
									checked={allSelected}
									indeterminate={isIndeterminate}
									onChange={(e) => handleSelectAll(e.target.checked)}
									inputProps={{
										"aria-label": allSelected
											? i18nPackage.t("editQuizPage.accommodationsStep.checkboxAriaLabel.unselectAll", { title })
											: i18nPackage.t("editQuizPage.accommodationsStep.checkboxAriaLabel.selectAll", { title }),
									}}
									disabled={visibleStudents.filter((s) => s.type !== "institution").length === 0}
								/>
								{students.length === 0 ? (
									<div className={styles.titleWrapper}>
										<div className={styles.title}>{title}</div>
									</div>
								) : (
									<div
										className={styles.titleWrapper}
										tabIndex={0}
										onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
											}
										}}
										role="button"
									>
										<div className={styles.title}>{title}</div>
										<div className={styles.titleIcons}>
											<CaretUp className={`${styles.titleIcon} ${sortDirection === "asc" ? styles.activeIcon : ""}`} />
											<CaretDown
												className={`${styles.titleIcon} ${sortDirection === "desc" ? styles.activeIcon : ""}`}
											/>
										</div>
									</div>
								)}
							</div>
							{actions && !isMobileLayout && (
								<div className={styles.actionsHeader}>
									{i18nPackage.t("editQuizPage.accommodationsStep.studentList.actionsHeader")}
								</div>
							)}
						</div>
						{students.length === 0 ? (
							<div
								className={styles.studentList}
								style={{
									borderTop: "none",
								}}
							>
								<div className={styles.noResultsWrapper}>
									{noResultsImage}
									<div className={styles.noResultsContainer}>
										<div className={styles.noResultsHeader}>{noResultsHeader}</div>
										<div className={styles.noResultsBody}>{noResultsBody}</div>
									</div>
								</div>
							</div>
						) : (
							<div className={styles.listBorder}>
								<div className={`${styles.listContainer} ${isMobileLayout ? styles.listContainerCompact : ""}`}>
									<List className={styles.studentList}>
										{visibleStudents.map((student, index) => (
											<StudentRow
												key={student.userId}
												student={student}
												selected={selected.has(student.userId)}
												onCheckboxChange={handleSelect}
												action={
													!action && <StudentActionMenu student={student} buttonIcon={buttonIcon} actions={actions} />
												}
												isExempted={isExempted}
												profilesMap={profilesMap}
												hideBottomBorder={fullPage && index === visibleStudents.length - 1}
												editInstitutionExemptions={editInstitutionExemptions}
											/>
										))}
									</List>
								</div>
							</div>
						)}
					</Box>
				</div>
				<CorePagination
					count={totalPages}
					onChange={(_e, page) => setPage(page - 1)}
					defaultPage={1}
					size={"medium"}
					page={currPage + 1}
					paginationWrap={false}
					aria-label={paginationAriaLabel}
				/>
			</div>
		</>
	);
}

function StudentActionMenu({
	student,
	actions,
}: {
	student: ExemptedStudent;
	buttonIcon: ReactNode;
	actions: Array<{
		name: string;
		hide?: boolean | ((student: ExemptedStudent) => boolean);
		action: (student: ExemptedStudent) => void;
	}>;
}) {
	const [menuShowing, setMenuShowing] = useState(false);

	return (
		<CoreMenuButton
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			sx={{ marginLeft: "2px" }}
			aria-label={i18nPackage.t("editQuizPage.accommodationsStep.studentList.actionsAriaLabel")}
		>
			{actions.map((action) =>
				(typeof action.hide === "function" ? action.hide(student) : action.hide) ? null : (
					<CoreMenuItem
						key={action.name}
						onClick={() => {
							action.action(student);
							setMenuShowing(false);
						}}
					>
						<ListItemText primary={action.name} />
					</CoreMenuItem>
				),
			)}
		</CoreMenuButton>
	);
}

function ViewDetailsModal({
	currentStudent,
	optionRelatedInstituteSettings,
	profilesMap,
	verityInstituteSettings,
}: {
	currentStudent: ExemptedStudent;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	verityInstituteSettings: VerityInstituteSettings;
}) {
	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);
	const isMobileLayout = useMobileLayout();
	const { getAllApplications, getAllExtensions } = useContext(VerityApiContext);

	const profile = useMemo(
		() =>
			currentStudent.proctoringOptionProfileId ? profilesMap.get(currentStudent.proctoringOptionProfileId) : undefined,
		[currentStudent.proctoringOptionProfileId, profilesMap],
	);

	const { optionCategories, optionChildren } = useMemo(() => {
		let optionCategories: Record<string, (keyof ProctoringOption)[]> = {};
		let optionChildren: Map<string, NestedOption[]> = new Map();

		if (profile?.proctoringOption !== undefined) {
			const configurations = categorizeProctoringOptions(
				profile.proctoringOption,
				verityInstituteSettings,
				optionRelatedInstituteSettings,
			);
			optionCategories = configurations.optionCategories;
			optionChildren = configurations.optionChildren;
		}
		return {
			optionCategories,
			optionChildren,
		};
	}, [optionRelatedInstituteSettings, profile, verityInstituteSettings]);

	useEffect(() => {
		async function fetchApplicationData() {
			const application_list = await getAllApplications();
			setApplications(application_list);
		}

		async function fetchExtensionData() {
			const extension_list = await getAllExtensions();
			setExtensions(extension_list);
		}

		void fetchApplicationData();
		void fetchExtensionData();
	}, []);

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => <div className={styles.viewDetailsModalContainer}>{children}</div>}
		>
			{isMobileLayout && (
				<CoreTitleCard
					title={i18nPackage.t("editQuizPage.accommodationsStep.accommodationDetails")}
					description={i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.header")}
					style={{ minWidth: "300px", padding: "15px 10px 10px 10px" }}
					slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
				/>
			)}
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => (
					<CorePaper component="section" className={styles.viewDetailsModalPage} sx={{ padding: "10px" }}>
						{children}
					</CorePaper>
				)}
				whenFalse={(children) => <div className={styles.viewDetailsModalPage}>{children}</div>}
			>
				{!isMobileLayout && (
					<div className={styles.viewDetailsModalHeader}>
						{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.header")}
					</div>
				)}
				<div className={styles.detailsSectionWrapper}>
					<div className={styles.detailsSectionHeader}>
						{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.detailsHeader")}
					</div>
					<div className={styles.detailsSectionContent}>
						<div className={styles.detailsSectionContentEntry}>
							<div
								className={`
								${styles.detailsSectionContentEntryTitle}
								${isMobileLayout ? styles.detailsSectionContentEntryTitleCompact : ""}
							`}
							>
								{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.detailsEntryTitle")}
							</div>
							<div className={styles.detailsSectionContentEntryBody}>
								{currentStudent.name
									? currentStudent.name
									: i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.unknownStudent")}
							</div>
						</div>
						{currentStudent.proctoringOptionProfileId && (
							<div className={styles.detailsSectionContentEntry}>
								<div
									className={`
									${styles.detailsSectionContentEntryTitle}
									${isMobileLayout ? styles.detailsSectionContentEntryTitleCompact : ""}
								`}
								>
									{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.profileHeader")}
								</div>
								<div className={styles.detailsSectionContentEntryBody}>
									{profile?.name || i18nPackage.t("editQuizPage.accommodationsStep.unknownProfile")}
								</div>
							</div>
						)}
						<div className={styles.detailsSectionContentEntry}>
							<div
								className={`
								${styles.detailsSectionContentEntryTitle}
								${isMobileLayout ? styles.detailsSectionContentEntryTitleCompact : ""}
							`}
							>
								{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.accommodationHeader")}
							</div>
							<div className={styles.detailsSectionContentEntryBody}>
								{currentStudent.proctoringOptionProfileId
									? i18nPackage.t("editQuizPage.accommodationsStep.modifiedProctoring")
									: i18nPackage.t("editQuizPage.accommodationsStep.notProctored")}
							</div>
						</div>
						<div className={styles.detailsSectionContentEntry}>
							<div
								className={`
								${styles.detailsSectionContentEntryTitle}
								${isMobileLayout ? styles.detailsSectionContentEntryTitleCompact : ""}
							`}
							>
								{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.platformHeader")}
							</div>
							<div className={styles.detailsSectionContentEntryBody}>
								{getPlatformTextFromProctoringOptions(
									enforceConfiguration(
										DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
										profile?.proctoringOption ?? {},
										verityInstituteSettings,
										optionRelatedInstituteSettings,
									).proctoringOptions,
								)}
							</div>
						</div>
						{currentStudent.proctoringOptionProfileId && (
							<div className={styles.detailsSectionContentEntry}>
								<div
									className={`
									${styles.detailsSectionContentEntryTitle}
									${isMobileLayout ? styles.detailsSectionContentEntryTitleCompact : ""}
								`}
								>
									{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.profileLocationHeader")}
								</div>
								<div className={styles.detailsSectionContentEntryBody}>
									{profile?.instituteProfile
										? i18nPackage.t("editQuizPage.accommodationsStep.instituteLocation")
										: profile?.courseProfile
											? i18nPackage.t("editQuizPage.accommodationsStep.courseLocation")
											: profile?.userProfile
												? i18nPackage.t("editQuizPage.accommodationsStep.userLocation")
												: i18nPackage.t("editQuizPage.accommodationsStep.unknownLocation")}
								</div>
							</div>
						)}
					</div>
				</div>
				{currentStudent.proctoringOptionProfileId && Object.keys(optionCategories).length > 0 && (
					<div className={styles.configSectionWrapper}>
						<div className={styles.configSectionHeader}>
							{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.configurationsHeader")}
						</div>
						<div className={styles.configSectionContent}>
							<ConfigurationSummaryList
								optionCategories={optionCategories}
								optionChildren={optionChildren}
								applications={applications}
								extensions={extensions}
							/>
						</div>
					</div>
				)}
				<div className={styles.notesSectionWrapper}>
					<div className={styles.notesSectionHeader}>
						{i18nPackage.t("editQuizPage.accommodationsStep.viewDetails.accommodationNotesHeader")}
					</div>
					<div className={styles.notesSectionContent}>
						{currentStudent.accommodationNotes || i18nPackage.t("editQuizPage.accommodationsStep.noNotes")}
					</div>
				</div>
			</ConditionalWrapper>
		</ConditionalWrapper>
	);
}

export const ExemptionSection = ({
	courseId,
	loading,
	profilesMap,
	refreshProfiles,
	hasInstituteProfilePermissions,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	enforcedProfilesOnly,
	setUploadedExemptedStudentsMap,
	exemptionInfo,
	setExemptionInfo,
	setStudentsLoaded,
	editInstitutionExemptions,
}: ExemptionModalProps) => {
	const [currentStudentListPage, setCurrentStudentListPage] = useState(0);
	const [currentExemptionListPage, setCurrentExemptionListPage] = useState(0);
	const [selectAccommodationModalShowing, setSelectAccommodationModalShowing] = useState(false);
	const [viewDetailsModalShowing, setViewDetailsModalShowing] = useState(false);
	const [currentStudent, setCurrentStudent] = useState<ExemptedStudent>();

	const [selectedStudents, setSelectedStudents] = useState<Array<ExemptedStudent>>();
	const isMobileLayout = useMobileLayout();
	const studentTableId = useId();
	const exemptedTableId = useId();
	const exemptedTableRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (setStudentsLoaded && (exemptionInfo.exemptRoster.size > 0 || exemptionInfo.nonExemptRoster.size > 0)) {
			setStudentsLoaded(true);
		}
	}, [exemptionInfo.exemptRoster, exemptionInfo.nonExemptRoster, setStudentsLoaded]);

	useEffect(() => {
		if (exemptionInfo.exemptRoster.size >= 0 && exemptionInfo.nonExemptRoster.size >= 0) {
			setUploadedExemptedStudentsMap(exemptionInfo.exemptRoster);
		}
	}, [exemptionInfo.exemptRoster, exemptionInfo.nonExemptRoster]);

	const [searchFilter, setSearchFilter] = useState("");
	const sortFunction = (a: ExemptedStudent, b: ExemptedStudent) => a.name.localeCompare(b.name);
	const displayedStudents = Array.from(exemptionInfo.nonExemptRoster.values())
		.filter((student) => student.name.toLowerCase().includes(searchFilter))
		.sort(sortFunction);
	const displayedExemptions = Array.from(exemptionInfo.exemptRoster.values())
		.filter((student) => student.name.toLowerCase().includes(searchFilter))
		.sort(sortFunction);

	const saveHandler = (selectedProfileId: number | undefined, selectedNotes: string | undefined) => {
		if (!selectedStudents) return;

		setExemptionInfo((info) => {
			const updatedExemptRoster = new Map(info.exemptRoster);
			const updatedNonExemptRoster = new Map(info.nonExemptRoster);

			selectedStudents.forEach((student) => {
				updatedNonExemptRoster.delete(student.userId);

				student.proctoringOptionProfileId = selectedProfileId;
				student.accommodationNotes = selectedNotes;

				updatedExemptRoster.set(student.userId, student);
			});

			return {
				nonExemptRoster: updatedNonExemptRoster,
				exemptRoster: updatedExemptRoster,
			};
		});

		coreToast.dismiss();
		if (!editInstitutionExemptions) {
			if (selectedStudents?.length === 1) {
				const studentName = selectedStudents[0].name || "the test-taker";
				coreToast.success(
					i18nPackage.t("editQuizPage.accommodationsStep.accommodationsSaved_one", {
						name: studentName,
					}),
				);
			} else {
				coreToast.success(i18nPackage.t("editQuizPage.accommodationsStep.accommodationsSaved_other"));
			}
		}

		if (isMobileLayout) {
			requestAnimationFrame(() => {
				exemptedTableRef.current?.scrollIntoView({ block: "end" });
			});
		}
	};

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.accommodationsPage} sx={{ padding: "10px" }}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div style={{ width: "fit-content", height: "100%" }}>{children}</div>}
		>
			{!loading ? (
				<>
					<div
						style={
							isMobileLayout
								? {
										display: "flex",
										flexWrap: "wrap",
										justifyContent: "center",
										width: "100%",
										padding: "0px 0px 20px 0px",
										maxWidth: "100%",
										placeItems: "center",
										columnGap: "24px",
									}
								: {
										display: "flex",
										flexWrap: "wrap",
										justifyContent: "center",
										padding: "0px 30px 20px 15px",
										placeItems: "center",
										columnGap: "24px",
									}
						}
					>
						<div style={{ marginBottom: "16px", fontSize: "15px" }}>
							<div style={{ marginBottom: "16px" }}>
								{i18nPackage.t("editQuizPage.accommodationsStep.accommodationsHeader")}
							</div>
							<div>
								<div style={{ fontWeight: "bold", display: "inline" }}>
									{i18nPackage.t("editQuizPage.accommodationsStep.accommodationsDisclaimerBold")}
								</div>
								{i18nPackage.t("editQuizPage.accommodationsStep.accommodationsDisclaimer")}
							</div>
						</div>
						<CoreSearchFieldPaper>
							<CoreSearchField
								autoComplete="off"
								noForm
								label={i18nPackage.t("editQuizPage.accommodationsStep.searchPlaceholder")}
								onSearch={(value) => {
									setSearchFilter(value.toLowerCase());
									setCurrentStudentListPage(0);
								}}
								ariaControls={`${studentTableId} ${exemptedTableId}`}
								totalResults={displayedStudents.length + displayedExemptions.length}
								showSearchButton={!isMobileLayout}
								value={searchFilter}
							/>
						</CoreSearchFieldPaper>

						<div
							style={
								isMobileLayout
									? {
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											flexWrap: "wrap",
											flexBasis: "100%",
											width: "100%",
											maxWidth: "100%",
										}
									: {
											display: "flex",
											justifyContent: "center",
											flexWrap: "wrap",
											flexBasis: "100%",
											columnGap: "25px",
										}
							}
						>
							<div
								style={
									isMobileLayout
										? {
												flex: "1 0",
												justifyContent: "center",
												width: "100%",
												maxWidth: "100%",
												minWidth: "100%",
											}
										: {
												flex: "1 0",
												justifyContent: "center",
												width: "397px",
												maxWidth: "397px",
												minWidth: editInstitutionExemptions ? "385px" : "397px",
											}
								}
							>
								<StudentList
									id={studentTableId}
									students={displayedStudents}
									action={(students) => {
										if (students) {
											students?.forEach((student) => {
												setExemptionInfo((info) => {
													info.nonExemptRoster.delete(student.userId);
													return {
														nonExemptRoster: new Map(info.nonExemptRoster),
														exemptRoster: new Map(info.exemptRoster.set(student.userId, student)),
													};
												});
											});
										}
									}}
									currPage={currentStudentListPage}
									setPage={setCurrentStudentListPage}
									isExempted={false}
									buttonTitle={i18nPackage.t("editQuizPage.accommodationsStep.accommodateButton")}
									title={i18nPackage.t("editQuizPage.accommodationsStep.nonAccommodatedTitle")}
									noResultsHeader={
										exemptionInfo.nonExemptRoster.size === 0
											? i18nPackage.t("editQuizPage.accommodationsStep.noTestTakersAvailable")
											: i18nPackage.t("editQuizPage.accommodationsStep.noTestTakersFound")
									}
									noResultsBody={
										exemptionInfo.nonExemptRoster.size === 0
											? i18nPackage.t("editQuizPage.accommodationsStep.noTestTakersAvailableBody")
											: i18nPackage.t("editQuizPage.accommodationsStep.noTestTakersFoundBody")
									}
									noResultsImage={
										exemptionInfo.nonExemptRoster.size === 0 ? (
											<NoStudentsImage className={styles.noTestTakersImage} />
										) : (
											<NoResults className={styles.noResultsImage} />
										)
									}
									profilesMap={profilesMap}
									setSelectedStudents={setSelectedStudents}
									setAccommodateModalShowing={setSelectAccommodationModalShowing}
									paginationAriaLabel={i18nPackage.t(
										"editQuizPage.accommodationsStep.paginationAriaLabel.nonAccommodated",
									)}
									editInstitutionExemptions={editInstitutionExemptions}
								/>
							</div>
							<div
								ref={exemptedTableRef}
								style={
									isMobileLayout
										? {
												flex: "1 0",
												justifyContent: "center",
												width: "100%",
												maxWidth: "100%",
												minWidth: "100%",
											}
										: {
												flex: "1 0",
												justifyContent: "center",
												width: "397px",
												maxWidth: "397px",
												minWidth: editInstitutionExemptions ? "385px" : "397px",
											}
								}
							>
								<StudentList
									id={exemptedTableId}
									students={displayedExemptions}
									buttonIcon={<ThreeDots />}
									currPage={currentExemptionListPage}
									setPage={setCurrentExemptionListPage}
									actions={[
										{
											name: i18nPackage.t("editQuizPage.accommodationsStep.viewDetailsOption"),
											action: (student) => {
												setCurrentStudent(student);
												setViewDetailsModalShowing(true);
											},
										},
										{
											name: i18nPackage.t("editQuizPage.accommodationsStep.editAccommodation"),
											action: (student) => {
												setSelectedStudents([student]);
												setSelectAccommodationModalShowing(true);
											},
											hide: (student) => {
												return !editInstitutionExemptions && student.type === "institution";
											},
										},
										{
											name: i18nPackage.t("editQuizPage.accommodationsStep.removeTestTaker"),
											action: (student) => {
												student.proctoringOptionProfileId = undefined;
												student.accommodationNotes = undefined;
												setExemptionInfo((info) => {
													info.exemptRoster.delete(student.userId);
													return {
														nonExemptRoster: new Map(info.nonExemptRoster.set(student.userId, student)),
														exemptRoster: new Map(info.exemptRoster),
													};
												});
											},
											hide: (student) => {
												return !editInstitutionExemptions && student.type === "institution";
											},
										},
									]}
									isExempted={true}
									removeAction={(student) => {
										student.accommodationNotes = undefined;
										student.proctoringOptionProfileId = undefined;
										setExemptionInfo((info) => {
											info.exemptRoster.delete(student.userId);
											return {
												nonExemptRoster: new Map(info.nonExemptRoster.set(student.userId, student)),
												exemptRoster: new Map(info.exemptRoster),
											};
										});
									}}
									buttonTitle={i18nPackage.t("editQuizPage.accommodationsStep.removeButton")}
									title={i18nPackage.t("editQuizPage.accommodationsStep.accommodatedTitle")}
									noResultsHeader={
										exemptionInfo.exemptRoster.size === 0
											? i18nPackage.t("editQuizPage.accommodationsStep.accommodateTestTakers")
											: i18nPackage.t("editQuizPage.accommodationsStep.noTestTakersFound")
									}
									noResultsBody={
										exemptionInfo.exemptRoster.size === 0
											? i18nPackage.t("editQuizPage.accommodationsStep.accommodateTestTakersBody")
											: i18nPackage.t("editQuizPage.accommodationsStep.noTestTakersFoundBody")
									}
									noResultsImage={
										exemptionInfo.exemptRoster.size === 0 ? (
											<NoAccommodationsImage className={styles.noTestTakersImage} />
										) : (
											<NoResults className={styles.noResultsImage} />
										)
									}
									profilesMap={profilesMap}
									setSelectedStudents={setSelectedStudents}
									paginationAriaLabel={i18nPackage.t(
										"editQuizPage.accommodationsStep.paginationAriaLabel.accommodated",
									)}
								/>
							</div>
						</div>
					</div>
					{selectAccommodationModalShowing && selectedStudents && (
						<SelectAccommodationModal
							open={selectAccommodationModalShowing}
							profileList={Array.from(profilesMap.values())}
							refreshProfiles={refreshProfiles}
							activeProfileId={
								(selectedStudents && selectedStudents.length > 0
									? selectedStudents
											.map((student) => student.proctoringOptionProfileId)
											.filter((id) => id !== undefined)
									: []
								).length === 1
									? selectedStudents[0].proctoringOptionProfileId
									: undefined
							}
							studentAccommodationNotes={
								selectedStudents && selectedStudents.length > 0
									? selectedStudents.every(
											(student) => student.accommodationNotes === selectedStudents[0].accommodationNotes,
										)
										? selectedStudents[0].accommodationNotes
										: undefined
									: undefined
							}
							lmsCourseId={courseId}
							optionRelatedInstituteSettings={optionRelatedInstituteSettings}
							verityInstituteSettings={verityInstituteSettings}
							enforcedProfilesOnly={enforcedProfilesOnly}
							hasInstituteProfilePermissions={hasInstituteProfilePermissions}
							saveHandler={saveHandler}
							onClose={() => {
								setSelectedStudents(undefined);
								setSelectAccommodationModalShowing(false);
							}}
						/>
					)}
					{viewDetailsModalShowing && currentStudent && (
						<CoreModal
							title={isMobileLayout ? undefined : i18nPackage.t("editQuizPage.accommodationsStep.accommodationDetails")}
							onTopClose={() => setViewDetailsModalShowing(false)}
							open={viewDetailsModalShowing}
							minWidth={isMobileLayout ? undefined : "min(60%, 800px)"}
							widthFixed
							heightFixed={isMobileLayout}
							fullScreen={isMobileLayout}
							showTopCloseButton={!isMobileLayout}
							dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
							buttons={[
								{
									text: i18nPackage.t("editQuizPage.accommodationsStep.closeButton"),
									color: "secondary",
									onClick: () => setViewDetailsModalShowing(false),
								},
							]}
						>
							<ViewDetailsModal
								currentStudent={currentStudent}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								profilesMap={profilesMap}
								verityInstituteSettings={verityInstituteSettings}
							/>
						</CoreModal>
					)}
				</>
			) : (
				<div
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignContent: "center",
						marginTop: "100px",
					}}
				>
					<CoreSpinner size={100} />
				</div>
			)}
		</ConditionalWrapper>
	);
};
export default ExemptionSection;
